// English is the source of truth for the key set: every other locale is typed
// against `typeof` these objects, so a missing or extra key is a compile error
// (see de/common.ts and the i18next module augmentation in ../../i18next.d.ts).
//
// `common` holds cross-cutting, framework-level copy (the data-table, saved
// views, loading). Feature copy lives in the per-feature namespaces.
//
// NB: `table.*` entries whose text contains single-brace tokens like `{count}`
// or `{column}` are filled by the table framework's own `fill()`/`.replace()`
// helper, NOT by i18next — keep the single braces. Entries with `{{double}}`
// braces are interpolated by i18next.
export const common = {
  loading: 'Loading…',

  table: {
    searchPlaceholder: 'Search...',
    clearSearch: 'Clear search',
    filters: 'Filters',
    columns: 'Columns',
    allOf: 'All {{label}}',
    clearAllFilters: 'Clear all',
    clearFilters: 'Clear filters',
    filtersActiveOne: '1 filter active',
    filtersActiveMany: '{count} filters active',
    filterByColumn: 'Filter by {column}',
    filterSearchPlaceholder: 'Search {column}…',
    resetFilter: 'Reset filter',
    filterNoMatch: 'No match.',
    filterMoreOptions: '+{count} more — narrow the search to see them.',
    toggleColumns: 'Toggle columns',
    columnAlwaysVisible: 'Always visible',
    resetColumns: 'Reset to default',
    searchColumns: 'Search columns…',
    noColumnsFound: 'No columns found.',
    noResults: 'No results',
    noResultsDescription: 'Try adjusting your search or filters.',
    loadFailed: 'Failed to load.',
    retry: 'Try again',
    rowsPerPage: 'Rows per page',
    pageOf: 'Page {{current}} of {{total}}',
    previousPage: 'Previous page',
    nextPage: 'Next page',
    resultOne: 'result',
    resultOther: 'results',
    resultsSuffix: 'found',
    export: 'Export CSV',
    exporting: 'Exporting…',
    exportTooLarge: 'Too many rows to export. Narrow the filters and try again.',
  },

  listView: {
    rename: 'Rename',
    delete: 'Delete',
    deleteViewTitle: 'Delete view “{{name}}”?',
    deleteViewDescription: 'This removes the saved view. Your current list stays as it is.',
    cancel: 'Cancel',
    renameDialogTitle: 'Rename view',
    viewNamePlaceholder: 'View name',
    save: 'Save',
    saveCurrentViewTitle: 'Save current view',
    saveViewButton: 'Save view',
    optionsFor: 'Options for {{name}}',
    viewFallback: 'view',
  },

  accessDenied: {
    title: 'You do not have access to this page',
    description:
      'Your account is missing a permission this page requires. An administrator of your organisation can grant it.',
    back: 'Back to overview',
  },

  // Display copy for the stable machine codes of the unified error contract
  // (backend: src/common/errors), resolved by `lib/field-errors.ts`. One entry
  // per code as codes appear; the server's message is a fallback only.
  fieldErrors: {
    brandMissing: 'Brand is a mandatory field.',
    modelMissing: 'Model is a mandatory field.',
    countryCodeMissing: 'Registration country is a mandatory field.',
    drivetrainMissing: 'Drivetrain is a mandatory field.',
    isNotEmpty: 'Required',
    isEmail: 'Enter a valid email address',
    duplicatedVin: 'A vehicle with this VIN already exists.',
    duplicatedVinInFleet: 'This VIN already belongs to {{vehicle}} ({{plate}}).',
    hsnInvalidFormat: 'The HSN consists of exactly 4 digits — include leading zeros.',
    tsnInvalidFormat: 'The TSN consists of exactly 3 letters or digits.',
    plateInvalidLength: 'The licence plate must be 2 to 15 characters long.',
    plateInvalidCharacters: 'Only letters, digits, spaces and hyphens are allowed.',
    mileageDateInFuture: 'The reference date cannot be in the future.',
    mileageDateBeforeFirstRegistration: 'Lies before the first registration.',
    productionDateInFuture: 'The production date cannot be in the future.',
    doorsOutOfRange: 'Doors must be between 1 and 6.',
    seatsOutOfRange: 'Seats must be between 1 and 9.',
    powerNotPositive: 'Power must be greater than 0 kW.',
    enginePowerAboveSystemPower: 'Engine power cannot exceed system power — it is part of it.',
    displacementNotPositive: 'Displacement must be greater than 0 cm³.',
    consumptionNegative: 'Consumption cannot be negative.',
    electricConsumptionNegative: 'Electricity consumption cannot be negative.',
    co2Negative: 'CO₂ emissions cannot be negative.',
    adBlueCapacityNotPositive: 'The AdBlue capacity must be greater than 0 l.',
    storageCapacityNotPositive: '{{storage}}: {{source}} needs a capacity greater than 0 {{unit}}.',
    consumptionEntryNegative: '{{source}}: consumption cannot be negative.',
    wheelTireCombinationMissingSize: 'A wheel/tire combination needs a tire size.',
    rangeReversed: 'The largest value is below the smallest.',
    grossMassBelowKerbWeight:
      'The permissible gross mass cannot be below the kerb weight — that would give a negative payload.',
    displacementNotApplicable: 'A purely electric vehicle has no displacement.',
    co2NotApplicable: 'An electric vehicle emits 0 g/km CO₂ at the tailpipe.',
    adBlueNotApplicable: 'This drivetrain carries no AdBlue.',
    fieldNotApplicableForDrivetrain: 'This drivetrain does not carry this field.',
    derivedValueMismatch: 'Does not match the drivetrain.',
    conflictsWithHiddenFields: "Conflicts with fields you don't have access to.",
    serviceDateBeforeRegistration: 'The last service cannot be before the first registration.',
    writeForbidden: 'You are not allowed to change this field.',
    duplicatedLicensePlate: 'An active vehicle with this licence plate already exists.',
    invalidVin: 'Enter a valid 17-character VIN (letters I, O and Q are never used).',
    missingRequiredField: 'Required',
    orgUnitNotInTenant: 'This organisational unit does not belong to your organisation.',
    groupNotInTenant: 'This group does not belong to your organisation.',
    mileageExceedsMaximum: 'Mileage cannot exceed 2,000,000 km.',
    mileageBelowMinimum: 'Mileage cannot be negative.',
    mileageDecreaseRequiresReason: 'Lowering the mileage requires a correction reason.',
    duplicatedLocationName: 'A location with this name already exists',
    assignmentStartInFuture: 'The assignment cannot start in the future.',
    driverAssignmentOverlap: "The assignment cannot start before the driver's previous assignment ended.",
    vehicleAssignmentOverlap: "The assignment cannot start before the vehicle's previous assignment ended.",
    contractPositionDateOrder: 'Valid-until date cannot be before valid-from date.',
    contractPositionOverlap: "This period overlaps the vehicle's other period on this contract.",
  },
};

export type CommonResources = typeof common;
